import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_16338d16-af9f-4ec6-a8f3-cd6da42efe59/artifacts/7xtaj2mx_WhatsApp%20Image%202026-03-02%20at%203.01.19%20PM%20%281%29.jpeg";

// Helper function to load image as base64
const loadImageAsBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load logo:', error);
    return null;
  }
};

export const generateSleepReport = async (entry) => {
  const doc = new jsPDF();
  
  // Colors
  const primaryColor = [37, 99, 235]; // Blue
  const darkBlue = [30, 58, 95]; // Dark blue for SleepMed text
  const greyText = [100, 116, 139]; // Grey for tagline
  const goodColor = [22, 101, 52]; // Green
  const moderateColor = [146, 64, 14]; // Orange
  const poorColor = [153, 27, 27]; // Red
  
  // Get classification color
  const getClassificationColor = () => {
    switch (entry.sleep_classification) {
      case 'Good': return goodColor;
      case 'Moderate': return moderateColor;
      case 'Poor': return poorColor;
      default: return [0, 0, 0];
    }
  };

  // Helper to format minutes to hours and minutes
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours} hours ${mins} minutes`;
  };

  let yPos = 15;
  
  // ========== HEADER SECTION ==========
  // Logo on TOP RIGHT
  try {
    const logoBase64 = await loadImageAsBase64(LOGO_URL);
    if (logoBase64) {
      // Position logo on right side (x=150, y=10, width=45, height=45)
      doc.addImage(logoBase64, 'JPEG', 150, 10, 45, 45);
    }
  } catch (error) {
    console.error('Could not add logo to PDF:', error);
  }
  
  // SleepMed text on LEFT (large, dark blue, bold)
  doc.setTextColor(...darkBlue);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('SleepMed', 14, 28);
  
  // Tagline below SleepMed (smaller, grey)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...greyText);
  doc.text('Laboratory · Solutions · Education', 14, 36);
  
  // Blue divider line (full width)
  yPos = 55;
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(1.5);
  doc.line(14, yPos, 196, yPos);
  
  // Report Title (centered, blue, bold)
  yPos += 15;
  doc.setTextColor(...primaryColor);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Sleep Diary Report', 105, yPos, { align: 'center' });
  
  // Subtitle (centered, grey)
  yPos += 8;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...greyText);
  doc.text('Two-Week Sleep Diary Program', 105, yPos, { align: 'center' });

  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  // Patient Information Section
  yPos += 15;
  
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Patient Information', 14, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  
  const patientInfo = [
    ['Name', entry.subject_name],
    ['Age', `${entry.age} years`],
    ['Phone', entry.phone_number || 'N/A'],
    ['Emergency Contact', entry.emergency_contact || 'N/A'],
    ['Date', entry.date],
    ['Day Type', entry.day_type.charAt(0).toUpperCase() + entry.day_type.slice(1)]
  ];
  
  patientInfo.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, 14, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 50, yPos);
    yPos += 6;
  });

  // Pre-Sleep Consumption (if any)
  if (entry.consumption_items && entry.consumption_items.length > 0 && !entry.consumption_items.includes("None")) {
    yPos += 6;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('Pre-Sleep Consumption', 14, yPos);
    
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('Items:', 14, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(180, 83, 9); // Amber color for warning
    doc.text(entry.consumption_items.join(', '), 35, yPos);
    
    if (entry.consumption_timing) {
      yPos += 6;
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'bold');
      doc.text('Timing:', 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(entry.consumption_timing, 35, yPos);
    }
  }

  // Sleep Data Section
  yPos += 10;
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Sleep Diary Responses', 14, yPos);
  
  yPos += 3;
  
  const sleepData = [
    ['Question', 'Response'],
    ['What time did you go to bed?', entry.bed_time],
    ['How long to fall asleep?', entry.sleep_latency],
    ['Night awakenings count', entry.wake_count],
    ['Total awakening duration', entry.wake_duration],
    ['Final awakening time', entry.final_wake_time],
    ['Time got out of bed', entry.get_up_time],
    ['Daytime nap', entry.nap],
    ['Sleep quality rating', entry.quality]
  ];

  autoTable(doc, {
    startY: yPos,
    head: [sleepData[0]],
    body: sleepData.slice(1),
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9
    },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 75 },
      1: { cellWidth: 85 }
    },
    margin: { left: 14, right: 14 }
  });

  // Calculated Metrics Section
  yPos = doc.lastAutoTable.finalY + 12;
  
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Sleep Analysis Results', 14, yPos);
  
  yPos += 3;
  
  const metricsData = [
    ['Metric', 'Value'],
    ['Total Time in Bed (TIB)', formatDuration(entry.total_time_in_bed_minutes)],
    ['Total Sleep Time (TST)', formatDuration(entry.total_sleep_time_minutes)],
    ['Sleep Efficiency', `${entry.sleep_efficiency}%`],
    ['Classification', entry.sleep_classification]
  ];

  autoTable(doc, {
    startY: yPos,
    head: [metricsData[0]],
    body: metricsData.slice(1),
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9
    },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    didParseCell: function(data) {
      // Color the classification cell
      if (data.row.index === 3 && data.column.index === 1) {
        data.cell.styles.textColor = getClassificationColor();
        data.cell.styles.fontStyle = 'bold';
      }
      // Color efficiency based on value
      if (data.row.index === 2 && data.column.index === 1) {
        if (entry.sleep_efficiency >= 85) {
          data.cell.styles.textColor = goodColor;
        } else if (entry.sleep_efficiency >= 70) {
          data.cell.styles.textColor = moderateColor;
        } else {
          data.cell.styles.textColor = poorColor;
        }
        data.cell.styles.fontStyle = 'bold';
      }
    },
    margin: { left: 14, right: 14 }
  });

  // Classification Legend
  yPos = doc.lastAutoTable.finalY + 12;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(60, 60, 60);
  doc.text('Sleep Efficiency Classification:', 14, yPos);
  
  yPos += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  doc.setTextColor(...goodColor);
  doc.text('Good: 85% or higher - Healthy sleep efficiency', 20, yPos);
  
  yPos += 5;
  doc.setTextColor(...moderateColor);
  doc.text('Moderate: 70-84% - Room for improvement', 20, yPos);
  
  yPos += 5;
  doc.setTextColor(...poorColor);
  doc.text('Poor: Below 70% - Consult a sleep specialist', 20, yPos);

  // Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text('Generated by SleepMediary', 105, pageHeight - 12, { align: 'center' });
  doc.text(`Report generated on: ${new Date().toLocaleString()}`, 105, pageHeight - 8, { align: 'center' });

  // Auto-download the PDF directly to PC (Chrome compatible)
  const fileName = `Sleep_Report_${entry.subject_name.replace(/\s+/g, '_')}_${entry.date}.pdf`;
  
  // Open PDF in new tab - user can save from there
  const pdfBlob = doc.output('blob');
  const blobUrl = window.URL.createObjectURL(pdfBlob);
  
  // Open in new tab
  const newWindow = window.open(blobUrl, '_blank');
  
  // If popup was blocked, try direct download
  if (!newWindow || newWindow.closed) {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  return fileName;
};

export const generateCSVReport = (entry) => {
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const csvContent = [
    ['Sleep Diary Report'],
    [''],
    ['Patient Information'],
    ['Name', entry.subject_name],
    ['Age', entry.age],
    ['Date', entry.date],
    ['Day Type', entry.day_type],
    [''],
    ['Sleep Diary Responses'],
    ['Bed Time', entry.bed_time],
    ['Time to Fall Asleep', entry.sleep_latency],
    ['Night Awakenings', entry.wake_count],
    ['Awakening Duration', entry.wake_duration],
    ['Final Wake Time', entry.final_wake_time],
    ['Time Got Out of Bed', entry.get_up_time],
    ['Daytime Nap', entry.nap],
    ['Sleep Quality', entry.quality],
    [''],
    ['Calculated Metrics'],
    ['Total Time in Bed', formatDuration(entry.total_time_in_bed_minutes)],
    ['Total Sleep Time', formatDuration(entry.total_sleep_time_minutes)],
    ['Sleep Efficiency', `${entry.sleep_efficiency}%`],
    ['Classification', entry.sleep_classification],
    [''],
    ['Report Generated', new Date().toLocaleString()]
  ];

  const csvString = csvContent.map(row => row.join(',')).join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  const fileName = `Sleep_Report_${entry.subject_name.replace(/\s+/g, '_')}_${entry.date}.csv`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  return fileName;
};
